import { DatePipe, PercentPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService, WorkflowIntentEvaluationRunDetail } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-evaluation-run-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    CardModule,
    TableModule,
    TagModule,
    DatePipe,
    PercentPipe
  ],
  template: `
    <ain-page [title]="t('workflow.intents.eval.runDetail.title')" [description]="t('workflow.intents.eval.runDetail.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else if (detail()) {
        <p-card [header]="t('workflow.intents.eval.runDetail.summary')">
          <div class="summary-grid">
            <div><strong>{{ t('workflow.intents.eval.columns.datasetKey') }}:</strong> {{ detail()!.run.datasetKey }}</div>
            <div><strong>{{ t('columns.status') }}:</strong> {{ detail()!.run.status }}</div>
            <div><strong>{{ t('workflow.intents.eval.columns.startedAt') }}:</strong> {{ detail()!.run.startedAtUtc | date: 'short' }}</div>
            <div><strong>{{ t('workflow.intents.eval.columns.completedAt') }}:</strong> {{ detail()!.run.completedAtUtc ? (detail()!.run.completedAtUtc | date: 'short') : '-' }}</div>
            <div><strong>{{ t('workflow.intents.eval.metrics.classificationAccuracy') }}:</strong> {{ detail()!.metrics?.classificationAccuracyProxy ?? 0 | percent: '1.0-1' }}</div>
            <div><strong>{{ t('workflow.intents.eval.metrics.slotCompleteness') }}:</strong> {{ detail()!.metrics?.slotFillCompleteness ?? 0 | percent: '1.0-1' }}</div>
          </div>
        </p-card>

        <p-card [header]="t('workflow.intents.eval.runDetail.results')">
          @if (detail()!.results.length === 0) {
            <ain-state type="empty" [title]="t('workflow.intents.eval.states.noResults')" />
          } @else {
            <p-table [value]="detail()!.results">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('workflow.intents.eval.columns.actualIntent') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.intentMatch') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.slotCompleteness') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.candidateHit') }}</th>
                  <th>{{ t('workflow.intents.eval.columns.safetyMatch') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.actualIntentKey }}</td>
                  <td><p-tag [value]="row.isIntentMatch ? 'OK' : 'Mismatch'" [severity]="row.isIntentMatch ? 'success' : 'danger'"></p-tag></td>
                  <td>{{ row.slotFillCompleteness | percent: '1.0-1' }}</td>
                  <td><p-tag [value]="row.isCandidateHit ? 'OK' : 'Mismatch'" [severity]="row.isCandidateHit ? 'success' : 'warn'"></p-tag></td>
                  <td><p-tag [value]="row.isSafetyMatch ? 'OK' : 'Mismatch'" [severity]="row.isSafetyMatch ? 'success' : 'danger'"></p-tag></td>
                </tr>
              </ng-template>
            </p-table>
          }
        </p-card>
      }
    </ain-page>
  `,
  styles: [`
    .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 8px 16px; }
  `]
})
export class WorkflowIntentEvaluationRunDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly detail = signal<WorkflowIntentEvaluationRunDetail | null>(null);

  ngOnInit(): void {
    const runId = this.route.snapshot.paramMap.get('runId');
    if (!runId) {
      this.error.set(this.t('common.invalidRequest'));
      this.loading.set(false);
      return;
    }

    this.api.getIntentEvaluationRun(runId).subscribe({
      next: detail => {
        this.detail.set(detail);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
